import { buildLocationsExport } from './mappers/locations.mapper';
import { buildScreensExport } from './mappers/screens.mapper';
import { buildVenuesExport } from './mappers/venues.mapper';

import type { Context, ListType } from '../multiFlightMap.types';
import type { GroupNameResolver, ListExport } from './export.types';
import type { PoiInfo } from './mappers/venues.mapper';

/** `selected`: the selected items of the list. `all`: every (non deleted) item, ignoring search and filters. */
export type ExportScope = 'selected' | 'all';

export type ListExportData = Pick<
  Context,
  'activeSubsection' | 'fullShapes' | 'selectedItems' | 'temporaryScreens' | 'temporaryVenues'
>;

const isVenueSubsection = (value: string | null): value is 'attributionPOIs' | 'proximityPoints' =>
  value === 'attributionPOIs' || value === 'proximityPoints';

/** Ids (the key each mapper matches on) of every non deleted item of the list. */
export const getAllItemIds = (list: ListType, data: ListExportData): string[] => {
  switch (list) {
    case 'screens':
      return data.temporaryScreens.filter(screen => !screen.deleted).map(screen => String(screen.source_element_id));
    case 'locations':
      return data.fullShapes.filter(zone => !zone.deleted).map(zone => String(zone._id));
    case 'venues':
    default:
      return data.temporaryVenues.filter(venue => !venue.deleted).map(venue => String(venue._id));
  }
};

export const getScopeIds = (list: ListType, scope: ExportScope, data: ListExportData): string[] =>
  scope === 'all' ? getAllItemIds(list, data) : (data.selectedItems[list] ?? []);

/** Brand and Category are only part of the attribution POIs export (they need a network lookup). */
export const includesBrandAndCategory = (list: ListType, data: ListExportData): boolean =>
  list === 'venues' && data.activeSubsection === 'attributionPOIs';

/** Table and file name of the items of the scope. `poiInfo` only matters for attribution POIs. */
export const buildListExport = (
  list: ListType,
  ids: string[],
  data: ListExportData,
  groupNames: GroupNameResolver,
  poiInfo?: Record<string, PoiInfo>
): { exported: ListExport; fileName: string } => {
  switch (list) {
    case 'screens':
      return { exported: buildScreensExport(data.temporaryScreens, ids, groupNames), fileName: 'screens' };
    case 'locations':
      return { exported: buildLocationsExport(data.fullShapes, ids, groupNames), fileName: 'locations' };
    case 'venues':
    default:
      return {
        exported: buildVenuesExport(data.temporaryVenues, ids, groupNames, {
          includeBrandAndCategory: includesBrandAndCategory(list, data),
          poiInfo
        }),
        fileName: isVenueSubsection(data.activeSubsection) ? data.activeSubsection : 'venues'
      };
  }
};
